import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, Calendar, Loader, AlertCircle } from 'lucide-react';

const ThankYouEvent = ({ event: propEvent, registrationId: propRegistrationId, participant: propParticipant }) => {
    const { eventId, participantId } = useParams();
    const [event, setEvent] = useState(propEvent || null);
    const [participant, setParticipant] = useState(propParticipant || null);
    const [loading, setLoading] = useState(!propEvent && !propParticipant);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            if (propEvent && propParticipant) {
                setLoading(false);
                return;
            }

            if (!participantId) {
                // If no participantId in params and no props, we can't show much
                // But if used as a route, we expect params. 
                // If used as component without props, it might be an issue.
                return;
            }

            try {
                // Fetch Participant details (which includes eventId)
                const participantRes = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/participants/public/${participantId}`);
                const participantData = await participantRes.json();

                if (!participantData.success) {
                    throw new Error(participantData.message || 'Failed to fetch participant');
                }

                setParticipant(participantData.data.participant);

                if (participantData.data.event) {
                    setEvent(participantData.data.event);
                } else if (eventId) {
                    const eventRes = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/public/${eventId}`);
                    const eventData = await eventRes.json();
                    if (eventData.success) {
                        setEvent(eventData.data);
                    }
                }

            } catch (err) {
                console.error('Error fetching details:', err);
                setError('Could not load registration details. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [eventId, participantId, propEvent, propParticipant]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f6f9]">
                <div className="text-center">
                    <Loader className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading registration details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f6f9] p-4">
                <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    const registrationId = propRegistrationId || participant?.registrationId;
    // Get visible name: from participant.data (dynamic fields) or participant.name (fallback)
    // Note: participant object structure from API might differ slightly from formData structure.
    // API usually returns { _id, data: { name: ... }, ... }
    // But formData was flat. Let's handle both.
    const participantName = participant?.data?.name || participant?.name || 'Participant';
    const participantEmail = participant?.data?.email || participant?.email;

    return (
        <div className="min-h-screen bg-[#f4f6f9] py-10 px-4 font-sans">
            <div className="max-w-[600px] mx-auto bg-white rounded-xl shadow-[0_8px_25px_rgba(0,0,0,0.08)] overflow-hidden">

                {/* Header */}
                <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-10 text-center text-white">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                        <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold m-0">Thank You, {participantName}!</h1>
                    <p className="mt-2 text-white/90 text-lg">Registration Successful</p>
                </div>

                {/* Content */}
                <div className="p-10 text-gray-800 leading-relaxed text-base">
                    <p className="mt-0">
                        Dear <strong>{participantName}</strong>,
                    </p>

                    <p className="mt-4">
                        You have successfully registered for <strong>{event?.eventName}</strong>.
                        We are excited to have you join us!
                    </p>

                    {/* Event Details Card */}
                    <div className="bg-gray-50 rounded-lg p-6 mt-6 border border-gray-100">
                        <div className="flex items-start space-x-3 mb-3">
                            <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
                            <div>
                                <span className="block font-semibold text-gray-900">Date & Time</span>
                                <span className="text-gray-600">
                                    {event?.eventStartDate && new Date(event.eventStartDate).toLocaleDateString()}
                                    {event?.eventEndDate && event.eventStartDate !== event.eventEndDate && ` - ${new Date(event.eventEndDate).toLocaleDateString()}`}
                                </span>
                            </div>
                        </div>
                        {registrationId && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <p className="text-sm text-gray-500 mb-1">Registration ID</p>
                                <p className="text-xl font-mono font-bold text-blue-600">{registrationId}</p>
                            </div>
                        )}
                    </div>

                    <p className="mt-6">
                        A confirmation email with your ticket details has been sent to <strong>{participantEmail}</strong>.
                    </p>

                    <div className="mt-8 text-center">
                        <button
                            onClick={() => window.location.href = `/register/${event._id}`}
                            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors no-underline"
                        >
                            Register Another Person
                        </button>
                    </div>

                    {/* ID Card Link Section */}
                    {participant && participant._id && (
                        <div className="mt-8 bg-blue-50 border border-blue-100 rounded-lg p-6 text-center">
                            <h3 className="text-sm font-semibold text-blue-900 mb-4 uppercase tracking-wide">
                                Your ID Card is Ready
                            </h3>

                            <a
                                href={`/v2/participant/${participant._id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                                </svg>
                                View ID Card
                            </a>

                            <p className="text-xs text-blue-800 mt-4">
                                Click to open your ID card in a new tab. You can download or print it from there.
                            </p>
                        </div>
                    )}

                    <p className="mt-8 mb-0 border-t pt-6">
                        With gratitude,<br />
                        <strong>Team {event?.eventName}</strong>
                    </p>
                </div>

                {/* Footer */}
                <div className="bg-[#f9fafb] p-5 text-center text-sm text-gray-500">
                    Sent with appreciation ❤️
                </div>

            </div>
        </div>
    );
};

export default ThankYouEvent;
